import { Badge, Card, Heading, Skeleton, Text } from '@ui/themed';
import { t } from 'i18next';
import { isShopify, type Platform, useEnv } from '../../../context/env-context';
import { useHandlers } from '../../../context/page-context';
import type { CustomerGroup } from '../types/customer-group-response';
import { PriceListBadges, ResultsElement } from './customer-groups-table';

function getTagHeadingKey(platform?: Platform) {
  if (isShopify(platform)) {
    return 'features:customer_group.components.customer_groups_base_card.headings.tag_shopify' as const;
  }
  if (platform === 'magento') {
    return 'features:customer_group.components.customer_groups_base_card.headings.tag_magento' as const;
  }
  if (platform === 'wix') {
    return 'features:customer_group.components.customer_groups_base_card.headings.tag_wix' as const;
  }
  return 'features:customer_group.components.customer_groups_base_card.headings.tag' as const;
}

export function CustomerGroupsBaseCard({
  baseGroup,
  isLoading,
}: {
  baseGroup?: CustomerGroup;
  isLoading?: boolean;
}) {
  const { navigationHandler } = useHandlers();
  const { platform } = useEnv();

  return (
    <Card>
      <Card.Header
        headingLevel="h2"
        primaryAction={{
          label: t('buttons:edit'),
          disabled: isLoading,
          onClick: () => navigationHandler.navigate('/customers/groups/edit/base'),
        }}
      >
        {t('features:customer_group.components.customer_groups_base_card.header')}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.customer_groups_base_card.description')}
        </Text>
        {isLoading || !baseGroup ? (
          <Skeleton />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            <div className="flex">
              <Card showBorder>
                <Card.Header headingLevel="h3">
                  <Heading icon="tag" level="h4" style="sm">
                    {t(getTagHeadingKey(platform))}
                  </Heading>
                </Card.Header>
                <Card.Body>
                  <div>
                    <Badge variant="green">{t('labels:b2b')}</Badge>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <Card showBorder>
              <Card.Header headingLevel="h3">
                <Heading icon="priceList" level="h4" style="sm">
                  {t(
                    'features:customer_group.components.customer_groups_base_card.headings.price_lists',
                  )}
                </Heading>
              </Card.Header>
              <Card.Body>
                <div className="flex flex-wrap gap-2">
                  <PriceListBadges group={baseGroup} />
                </div>
              </Card.Body>
            </Card>
            <Card showBorder>
              <Card.Header headingLevel="h3">
                <Heading icon="payment" level="h4" style="sm">
                  {t(
                    'features:customer_group.components.customer_groups_base_card.headings.payment_methods',
                  )}
                </Heading>
              </Card.Header>
              <Card.Body>
                <div className="flex flex-wrap gap-2">
                  <ResultsElement noLimit data={baseGroup.paymentMethods} />
                </div>
              </Card.Body>
            </Card>
            <Card showBorder>
              <Card.Header headingLevel="h3">
                <Heading icon="document" level="h4" style="sm">
                  {t(
                    'features:customer_group.components.customer_groups_base_card.headings.other_settings',
                  )}
                </Heading>
              </Card.Header>
              <Card.Body>
                <div className="flex flex-wrap gap-2">
                  {baseGroup.overwrittenSettings ? (
                    <Badge variant="yellow">{String(baseGroup.overwrittenSettings)}</Badge>
                  ) : (
                    <Badge variant="default">{t('labels:none')}</Badge>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
